// Aggregation layer between the bulk export endpoint and the XLSX / PDF
// writers. Turns the flat, two-basis row sets into the slices the workbooks are
// organised around: one slice per weekday, plus a combined "all weekdays" slice
// for each week / month / season roll-up.
//
// Running-day counts are carried separately from the value rows and never
// summed out of them — COUNT(DISTINCT date) can't be added across items without
// double counting, so `days` has its own grain in the payload and every per-day
// figure here divides by the matching slice's count.

import {
  DAYS, MONTHS, mergeRows, sumRows, perDay,
  type ExportPayload, type ExportBasis, type ExpKey, type MergedRow,
} from './researchShared'

/** One weekday (or the combined roll-up) of a week / month / season. */
export interface Slice {
  /** 1-7 for a weekday, or 0 for the combined "all weekdays" roll-up. */
  dayNo: number
  label: string
  curDays: number
  prevDays: number
  items: MergedRow[]
  routesByItem: Map<string, MergedRow[]>
  services: MergedRow[]
  totals: ReturnType<typeof sumRows>
}

/** Which rows of the payload a given worksheet is built from. */
export interface SliceFilter {
  /** Week of month 1-4 (month scope only). */
  wom?: number
  /** Calendar year + month (season scope only). */
  yr?: number
  mo?: number
}

const matches = (k: ExpKey, f: SliceFilter): boolean =>
  (f.wom == null || k.wom === f.wom)
  && (f.yr == null || k.yr === f.yr)
  && (f.mo == null || k.mo === f.mo)

/** Group a list by a key, preserving insertion order. */
function groupBy<T, K>(list: T[], keyOf: (r: T) => K): Map<K, T[]> {
  const out = new Map<K, T[]>()
  for (const r of list) {
    const k = keyOf(r)
    const a = out.get(k) ?? []
    a.push(r)
    out.set(k, a)
  }
  return out
}

const svcKey = (r: { direction: string; dep_time: string; service: string }) =>
  `${r.direction}|${r.dep_time}|${r.service}`

/**
 * Build one slice.
 *
 * `dayNo` of 0 means "every weekday combined" — used for the week total, the
 * month total and the season-to-date sheet.
 */
function buildSlice(payload: ExportPayload, filter: SliceFilter, dayNo: number, label: string): Slice {
  const pick = <T extends ExpKey>(rows: T[]): T[] =>
    rows.filter((r) => matches(r, filter) && (dayNo === 0 || r.day_no === dayNo))

  const sumDays = (basis: ExportBasis): number =>
    pick(basis.days).reduce((t, r) => t + r.days, 0)

  const curItems = pick(payload.cur.items)
  const prevItems = pick(payload.prev.items)
  const curRoutes = pick(payload.cur.routes)
  const prevRoutes = pick(payload.prev.routes)
  const curSvc = pick(payload.cur.services)
  const prevSvc = pick(payload.prev.services)

  const items = mergeRows(curItems, prevItems, (r) => r.item, (r) => r.qty, (r) => r.net)

  const curByItem = groupBy(curRoutes, (r) => r.item)
  const prevByItem = groupBy(prevRoutes, (r) => r.item)
  const routesByItem = new Map<string, MergedRow[]>()
  for (const item of new Set([...curByItem.keys(), ...prevByItem.keys()])) {
    routesByItem.set(item, mergeRows(
      curByItem.get(item) ?? [], prevByItem.get(item) ?? [],
      (r) => r.route, (r) => r.qty, (r) => r.net,
    ))
  }

  const services = mergeRows(curSvc, prevSvc, svcKey, (r) => r.qty, (r) => r.net)

  return {
    dayNo,
    label,
    curDays: sumDays(payload.cur),
    prevDays: sumDays(payload.prev),
    items,
    routesByItem,
    services,
    totals: sumRows(items),
  }
}

/**
 * Every weekday slice that actually ran, plus the combined roll-up, for one
 * worksheet's worth of data. Weekdays with no running days in either year are
 * dropped — an empty Thursday sheet section is noise, not information.
 */
export function buildSheetSlices(payload: ExportPayload, filter: SliceFilter, rollupLabel: string): {
  days: Slice[]
  rollup: Slice
} {
  const days: Slice[] = []
  for (let d = 1; d <= 7; d++) {
    const s = buildSlice(payload, filter, d, DAYS[d - 1])
    if (s.curDays > 0 || s.prevDays > 0) days.push(s)
  }
  return { days, rollup: buildSlice(payload, filter, 0, rollupLabel) }
}

/** The weeks present in a month-scope payload, in order. */
export function weeksIn(payload: ExportPayload): number[] {
  const set = new Set<number>()
  for (const r of payload.cur.days) if (r.days > 0) set.add(r.wom)
  for (const r of payload.prev.days) if (r.days > 0) set.add(r.wom)
  return [...set].sort((a, b) => a - b)
}

/** The (year, month) pairs present in a season-scope payload, in season order. */
export function monthsIn(payload: ExportPayload): { yr: number; mo: number; label: string }[] {
  const set = new Map<string, { yr: number; mo: number }>()
  const take = (rows: ExpKey[]) => { for (const r of rows) set.set(`${r.yr}-${String(r.mo).padStart(2, '0')}`, { yr: r.yr, mo: r.mo }) }
  // Only this season's months become sheets; last year's rows exist purely as
  // the comparison basis and would otherwise add a phantom set of tabs.
  take(payload.cur.days.filter((r) => r.days > 0))
  return [...set.keys()].sort().map((k) => {
    const { yr, mo } = set.get(k)!
    return { yr, mo, label: `${MONTHS[mo - 1]} ${yr}` }
  })
}

// ---------------------------------------------------------------------------
//  Top ten
// ---------------------------------------------------------------------------

export interface TopRow {
  label: string
  curQty: number
  curNet: number
  prevQty: number
  prevNet: number
  /** Net £ per running day for the scope this row was computed over. */
  curPerDay: number
  prevPerDay: number
  /** Share of the scope's total net revenue this year. */
  sharePct: number
}

function toTop(rows: MergedRow[], curDays: number, prevDays: number, totalNet: number, limit: number): TopRow[] {
  return rows.slice(0, limit).map((r) => ({
    label: r.label,
    curQty: r.curQty,
    curNet: r.curNet,
    prevQty: r.prevQty,
    prevNet: r.prevNet,
    curPerDay: perDay(r.curNet, curDays),
    prevPerDay: perDay(r.prevNet, prevDays),
    sharePct: totalNet !== 0 ? (r.curNet / totalNet) * 100 : 0,
  }))
}

/** Top N services / ticket types for a slice, ranked by this year's net revenue. */
export function topServices(slice: Slice, limit = 10): TopRow[] {
  return toTop(slice.services, slice.curDays, slice.prevDays, slice.totals.curNet, limit)
}

export function topItems(slice: Slice, limit = 10): TopRow[] {
  return toTop(slice.items, slice.curDays, slice.prevDays, slice.totals.curNet, limit)
}

/** Split a service key back into its parts for display. */
export function splitSvc(key: string): { direction: string; depTime: string; service: string } {
  const [direction = '', depTime = '', service = ''] = key.split('|')
  return { direction, depTime, service }
}

/** Fetch a bulk export payload. */
export async function fetchExport(scope: 'month' | 'season', m?: number): Promise<ExportPayload> {
  const q = new URLSearchParams({ scope })
  if (scope === 'month') q.set('m', String(m))
  const res = await fetch(`/api/research/weekday-revenue/export?${q.toString()}`)
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error((b as { error?: string }).error || `Export request failed (${res.status})`)
  }
  return res.json() as Promise<ExportPayload>
}
